import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from "amazon-cognito-identity-js";

// Read credentials from Bun public env vars
const poolData = {
  UserPoolId: process.env.BUN_PUBLIC_COGNITO_USER_POOL_ID || "",
  ClientId: process.env.BUN_PUBLIC_COGNITO_WEB_CLIENT_ID || "",
};

export const userPool = new CognitoUserPool(poolData);

export interface SignInResult {
  type: "SUCCESS" | "NEW_PASSWORD_REQUIRED";
  session?: any;
  cognitoUser?: CognitoUser;
  userAttributes?: Record<string, string>;
}

export const signIn = (username: string, password: string): Promise<SignInResult> => {
  return new Promise((resolve, reject) => {
    const authenticationData = {
      Username: username,
      Password: password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);

    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (result) => {
        resolve({ type: "SUCCESS", session: result });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Clean up attributes that are not writable or required
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        delete userAttributes.email;
        delete userAttributes.phone_number;
        delete userAttributes.sub;

        const cleanAttributes: Record<string, string> = {};
        for (const attr of requiredAttributes) {
          if (userAttributes[attr]) {
            cleanAttributes[attr] = userAttributes[attr];
          }
        }

        resolve({
          type: "NEW_PASSWORD_REQUIRED",
          cognitoUser,
          userAttributes: cleanAttributes,
        });
      },
    });
  });
};

export const completeNewPassword = (
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string> = {}
): Promise<any> => {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

export const getCurrentSessionToken = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session.getAccessToken().getJwtToken());
    });
  });
};

export const getCurrentUserEmail = (): Promise<string | null> => {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err: any, session: any) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      try {
        resolve(session.getIdToken().payload.email || cognitoUser.getUsername());
      } catch {
        resolve(cognitoUser.getUsername());
      }
    });
  });
};

export const sendRecoveryCode = (username: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

export const confirmForgotPassword = (
  username: string,
  code: string,
  newPassword: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

export const signOutCurrentUser = (): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

export const signUpUser = (
  email: string,
  username: string,
  password: string,
  name: string,
  picture: string
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const attributeList: CognitoUserAttribute[] = [];

    const dataEmail = {
      Name: "email",
      Value: email,
    };
    const dataName = {
      Name: "name",
      Value: name,
    };
    const dataPicture = {
      Name: "picture",
      Value: picture,
    };

    attributeList.push(new CognitoUserAttribute(dataEmail));
    attributeList.push(new CognitoUserAttribute(dataName));
    attributeList.push(new CognitoUserAttribute(dataPicture));

    userPool.signUp(username, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const confirmSignUp = (username: string, code: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const resendSignUpCode = (username: string): Promise<any> => {
  return new Promise((resolve, reject) => {
    const userData = {
      Username: username,
      Pool: userPool,
    };
    const cognitoUser = new CognitoUser(userData);

    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result);
    });
  });
};

export const refreshSessionWithBackend = async (): Promise<string | null> => {
  try {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return null;

    const username = cognitoUser.getUsername();
    const clientId = userPool.getClientId();

    const refreshToken = await new Promise<string | null>((resolve) => {
      cognitoUser.getSession((err: any, session: any) => {
        if (err || !session) resolve(null);
        else resolve(session.getRefreshToken().getToken());
      });
    });

    if (!refreshToken) return null;

    const savedUserUrl = localStorage.getItem("user_service_url");
    const envUserUrl = process.env.BUN_PUBLIC_USER_SERVICE_URL || "http://localhost:3003";
    const userServiceUrl = (savedUserUrl || envUserUrl).replace(/\/$/, "");

    const response = await fetch(`${userServiceUrl}/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh session via backend: ${response.statusText}`);
    }

    const data = await response.json();
    localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`, data.id_token);
    localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`, data.access_token);
    if (data.refresh_token) {
      localStorage.setItem(`CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`, data.refresh_token);
    }

    return data.access_token;
  } catch (err) {
    console.error("Error refreshing session with backend:", err);
    return null;
  }
};

export const formatAuthError = (err: any): string => {
  if (!err) return "Ocurrió un error inesperado.";
  if (typeof err === "string") return err;

  const code = err.code || err.name || "";
  const message = err.message || "";

  if (code === "UsernameExistsException" || message.includes("User already exists") || message.includes("user already exists")) {
    return "Ya existe una cuenta registrada con este correo electrónico.";
  }
  if (code === "UserNotFoundException" || message.includes("User does not exist") || message.includes("not found")) {
    return "No existe ninguna cuenta registrada con este correo electrónico.";
  }
  if (code === "UserNotConfirmedException" || message.includes("User is not confirmed") || message.includes("not confirmed")) {
    return "Tu cuenta aún no ha sido confirmada. Verificá tu correo electrónico e ingresá el código.";
  }
  if (code === "PasswordResetRequiredException" || message.includes("Password reset required")) {
    return "Debes restablecer tu contraseña para continuar.";
  }
  if (code === "NotAuthorizedException" || message.includes("Incorrect username or password") || message.includes("not authorized")) {
    return "Correo electrónico o contraseña incorrectos.";
  }
  if (code === "CodeMismatchException" || message.includes("Invalid verification code") || message.includes("code mismatch")) {
    return "El código de verificación ingresado es incorrecto.";
  }
  if (code === "ExpiredCodeException" || message.includes("Expired code") || message.includes("code has expired")) {
    return "El código de verificación ha expirado. Por favor, solicitá uno nuevo.";
  }
  if (code === "InvalidPasswordException" || message.includes("Password did not conform") || message.includes("password policy")) {
    return "La contraseña no cumple con los requisitos de seguridad del servidor.";
  }
  if (code === "LimitExceededException" || message.includes("Attempt limit exceeded")) {
    return "Se excedió el límite de intentos permitidos. Por favor, intentá más tarde.";
  }
  if (code === "TooManyRequestsException" || message.includes("Too many requests")) {
    return "Demasiadas solicitudes en poco tiempo. Por favor, esperá unos minutos.";
  }
  if (code === "InvalidParameterException" || message.includes("Invalid parameter")) {
    return "Verificá que todos los datos ingresados sean correctos.";
  }
  if (message.includes("User is disabled")) {
    return "Esta cuenta ha sido desactivada o bloqueada.";
  }

  return message || "Ocurrió un error inesperado.";
};

